import { ApiResponse } from "./api";
import { API_URL } from "../config/api";
import {
  Team,
  PromotionCode,
  PaymentData,
  PaymentResponse,
  PaymentStatus,
  PaymentHistoryItem,
} from "../types/payment";

// Token management
const getToken = (): string | null => {
  return localStorage.getItem("token");
};

const getAuthHeaders = (): HeadersInit => {
  const token = getToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    (headers as any)["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

export class PaymentService {
  /**
   * Get payment information for a specific team
   */
  static async getPaymentInfo(teamId: number): Promise<ApiResponse<any>> {
    try {
      const response = await fetch(
        `${API_URL.BASE_API}payment/info?team_id=${teamId}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      // if (!response.ok) {
      //   const errorData = await response.json();
      //   console.log("errorData", errorData);
      //   return {
      //     success: false,
      //     message: errorData.message || "Failed to fetch payment information",
      //   };
      // }

      return await response.json();
    } catch (error) {
      console.error("Error fetching payment info:", error);
      return {
        success: false,
        message: "Failed to fetch payment information",
      };
    }
  }

  /**
   * Get user's teams with pricing information
   */
  static async getUserTeams(): Promise<ApiResponse<Team[]>> {
    try {
      const response = await fetch(`${API_URL.BASE_API}payment/teams`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching user teams:", error);
      return {
        success: false,
        message: "Failed to fetch teams",
      };
    }
  }

  /**
   * Validate promotion code
   */
  static async validatePromotionCode(
    code: string,
    teamId: number
  ): Promise<ApiResponse<PromotionCode>> {
    try {
      const response = await fetch(`${API_URL.BASE_API}payment/validate-code`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          code,
          team_id: teamId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || "Failed to validate promotion code",
        };
      }

      return await response.json();
    } catch (error) {
      console.error("Error validating promotion code:", error);
      return {
        success: false,
        message: "Failed to validate promotion code",
      };
    }
  }

  /**
   * Initiate payment
   */
  static async initiatePayment(
    paymentData: PaymentData
  ): Promise<ApiResponse<PaymentResponse>> {
    try {
      const response = await fetch(`${API_URL.BASE_API}payment/initiate`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(paymentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: errorData.message || "Failed to initiate payment",
          errors: errorData.errors,
        };
      }

      return await response.json();
    } catch (error) {
      console.error("Error initiating payment:", error);
      return {
        success: false,
        message: "Failed to initiate payment",
      };
    }
  }

  /**
   * Check payment status
   */
  static async checkPaymentStatus(
    paymentId: number
  ): Promise<ApiResponse<PaymentStatus>> {
    try {
      const response = await fetch(
        `${API_URL.BASE_API}payment/status/${paymentId}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error checking payment status:", error);
      return {
        success: false,
        message: "Failed to check payment status",
      };
    }
  }

  /**
   * Get payment history
   */
  static async getPaymentHistory(): Promise<ApiResponse<PaymentHistoryItem[]>> {
    try {
      const response = await fetch(`${API_URL.BASE_API}payment/history`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching payment history:", error);
      return {
        success: false,
        message: "Failed to fetch payment history",
      };
    }
  }

  /**
   * Open payment gateway in popup and handle payment flow
   */
  static async processPayment(
    gatewayUrl: string,
    paymentId: number,
    onStatusUpdate: (status: string) => void,
    onComplete: (success: boolean) => void
  ): Promise<void> {
    // Open payment gateway in popup
    const popup = window.open(
      gatewayUrl,
      "payment",
      "width=600,height=700,scrollbars=yes,resizable=yes"
    );

    if (!popup) {
      onComplete(false);
      return;
    }

    let pollInterval: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;

    // Listen for messages from popup
    const messageListener = (event: MessageEvent) => {
      if (event.data.type === "payment_result") {
        if (pollInterval) clearInterval(pollInterval);
        if (timeoutId) clearTimeout(timeoutId);

        window.removeEventListener("message", messageListener);
        popup.close();

        onComplete(event.data.status === "completed");
      }
    };

    window.addEventListener("message", messageListener);

    // Poll payment status every 3 seconds
    pollInterval = setInterval(async () => {
      try {
        const result = await PaymentService.checkPaymentStatus(paymentId);
        if (result.success && result.data) {
          onStatusUpdate(result.data.payment_status);

          if (
            ["completed", "failed", "timeout"].includes(
              result.data.payment_status
            )
          ) {
            clearInterval(pollInterval);
            clearTimeout(timeoutId);
            window.removeEventListener("message", messageListener);

            if (popup && !popup.closed) {
              popup.close();
            }

            onComplete(result.data.payment_status === "completed");
          }
        }
      } catch (error) {
        console.error("Error polling payment status:", error);
      }
    }, 3000);

    // Timeout after 3 minutes
    timeoutId = setTimeout(() => {
      if (pollInterval) clearInterval(pollInterval);
      window.removeEventListener("message", messageListener);

      if (popup && !popup.closed) {
        popup.close();
      }

      onComplete(false);
    }, 180000); // 3 minutes

    // Handle popup being closed manually - check every 500ms for better responsiveness
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        if (pollInterval) clearInterval(pollInterval);
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener("message", messageListener);

        // Start polling payment status after popup closes
        onStatusUpdate("checking");

        // Poll for payment status every 3 seconds after popup closes
        const afterCloseInterval = setInterval(async () => {
          try {
            const result = await PaymentService.checkPaymentStatus(paymentId);
            if (result.success && result.data) {
              onStatusUpdate(result.data.payment_status);

              if (
                ["completed", "failed", "timeout"].includes(
                  result.data.payment_status
                )
              ) {
                clearInterval(afterCloseInterval);
                onComplete(result.data.payment_status === "completed");
              }
            }
          } catch (error) {
            console.error(
              "Error checking payment status after popup close:",
              error
            );
          }
        }, 3000);

        // Stop checking after 2 minutes
        setTimeout(() => {
          clearInterval(afterCloseInterval);
          onComplete(false);
        }, 120000);
      }
    }, 500);
  }

  /**
   * Create VNPT Pay payment
   */
  static async createVNPTPayment(paymentData: {
    team_id: number;
    amount: number;
    buyer_first_name: string;
    buyer_last_name: string;
    buyer_phone: string;
    buyer_email: string;
    buyer_address: string;
    buyer_city: string;
    buyer_state: string;
    buyer_country: string;
  }): Promise<
    ApiResponse<{
      payment_id: number;
      order_code: string;
      payment_url: string;
      amount: number;
      currency: string;
    }>
  > {
    try {
      const response = await fetch(
        `${API_URL.BASE_API}vnptpay/create-payment`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(paymentData),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.message || "Failed to create VNPT Pay payment",
          errors: result.errors,
        };
      }

      return result;
    } catch (error) {
      console.error("Error creating VNPT Pay payment:", error);
      return {
        success: false,
        message: "Network error occurred while creating payment",
      };
    }
  }

  /**
   * Get VNPT Pay payment status
   */
  static async getVNPTPaymentStatus(orderCode: string): Promise<
    ApiResponse<{
      payment_id: number;
      order_code: string;
      team_id: number;
      amount: number;
      status: string;
      payment_method: string;
      vnpt_transaction_id: string | null;
      paid_at: string | null;
      created_at: string;
    }>
  > {
    try {
      const response = await fetch(
        `${API_URL.BASE_API}vnptpay/payment-status?order_code=${orderCode}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: result.message || "Failed to get payment status",
        };
      }

      return result;
    } catch (error) {
      console.error("Error getting VNPT Pay payment status:", error);
      return {
        success: false,
        message: "Network error occurred while checking payment status",
      };
    }
  }

  /**
   * Process VNPT Pay payment with popup and status polling
   */
  static async processVNPTPayment(
    paymentData: {
      team_id: number;
      amount: number;
      buyer_first_name: string;
      buyer_last_name: string;
      buyer_phone: string;
      buyer_email: string;
      buyer_address: string;
      buyer_city: string;
      buyer_state: string;
      buyer_country: string;
    },
    onStatusUpdate: (status: string) => void,
    onComplete: (success: boolean, orderCode?: string) => void
  ): Promise<void> {
    try {
      // Step 1: Create payment
      onStatusUpdate("creating");
      const createResult = await this.createVNPTPayment(paymentData);

      if (!createResult.success || !createResult.data) {
        onStatusUpdate("failed");
        onComplete(false);
        return;
      }

      const { payment_url, order_code } = createResult.data;

      // Step 2: Redirect to VNPT Pay gateway in current tab
      onStatusUpdate("redirecting");
      window.location.href = payment_url;
      
      // Note: After redirect, the current page will be replaced by payment gateway
      // Payment status will be handled by return URLs configured in the payment gateway
    } catch (error) {
      console.error("Error processing VNPT Pay payment:", error);
      onStatusUpdate("failed");
      onComplete(false);
    }
  }
}
